import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ReviewerService } from '../../core/services/reviewer.service';
import { MasterService } from '../../core/services/master.service';
import { RebarAuthService } from '../../core/rebarauth/rebar.auth.service';
import { UserRoleService } from '../../core/services/user-role.service';
import { DateFormatService } from '../../core/services/date-format.service';

export interface AddUserForm {
  requestType: string;
  email: string;
  firstName: string;
  lastName: string;
  rcFunction: string;
  country: string;
  workLocation: string;
  expirationOn: string;
}

export interface RequestDetailsForm {
  reviewId: string;
  reason: string;
  attachments: { fileName: string; storagePath: string }[];
  requestedBy: string;
  requestedDateTime: string;
  createdDateTime: string;
  modifiedDateTime: string;
  createdBy: string;
  modifiedBy: string;
  status: string;
  revisedTargetPublicationDate:string
}

@Component({
  standalone: true,
  selector: 'app-request-view-edit',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './request-view-edit.html',
  styleUrl: './request-view-edit.css',
})
export class RequestViewEdit implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private reviewerService = inject(ReviewerService);
  private destroyRef = inject(DestroyRef);
  private _activePicker: HTMLInputElement | null = null;
  private _pickerResizeHandler: (() => void) | null = null;
  private masterService = inject(MasterService);
  private rebarAuth = inject(RebarAuthService);
  private userRoleService = inject(UserRoleService);
  private dateFormatSvc = inject(DateFormatService);

  formatDateDisplay(value: string | null | undefined): string {
    return this.dateFormatSvc.format(value);
  }

  openDatePicker(input: HTMLInputElement): void {
    if (!input.disabled && !input.readOnly) {
      this._activePicker = input;
      input?.showPicker?.();
    }
  }


  requestId = '';
  requestType = 'Add New User';
  mode: 'view' | 'edit' = 'view';

  private rawRequestData: any = null;


  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  // Generated by GitHub Copilot - Oct-07-2026
  /** Uses the API status, falling back to the details form for other request types. */
  get isRequestCompleted(): boolean {
    const status = (this.status || this.requestDetailsForm.status).trim().toLowerCase();
    return status === 'approved' || status === 'rejected';
  }

  get isApprovalBlockedForCurrentUser(): boolean {
    if (this.formType !== 'reviewCancellation' && this.formType !== 'notApplicable' && this.formType !== 'revisedTargetDate') return false;
    if (this.userRoleService.isAdmin) return false;
    const createdBy = this.beforeAt(this.requestDetailsForm.createdBy).toLowerCase();
    const rcMgr     = (this.rcManager          ?? '').toLowerCase();
    const secRcMgr  = (this.secondaryRcManager ?? '').toLowerCase();
    const loggedIn  = (this.rebarAuth.getUserName() ?? '').toLowerCase();
    if (!createdBy || !loggedIn) return false;

    if (createdBy === rcMgr) {
      // Primary raised — secondary exists: only secondary approves; else only admin approves
      return secRcMgr ? loggedIn !== secRcMgr : !this.userRoleService.isAdmin;
    }

    if (createdBy === secRcMgr) {
      // Secondary raised — primary exists: only primary approves; else only admin approves
      return rcMgr ? loggedIn !== rcMgr : !this.userRoleService.isAdmin;
    }

    return false;
  }

  get isSelfReviewCancellationRequest(): boolean {
    if (this.formType !== 'reviewCancellation' && this.formType !== 'notApplicable' && this.formType !== 'revisedTargetDate') return false;
    if (this.userRoleService.isAdmin) return false;
    const createdBy  = this.beforeAt(this.requestDetailsForm.createdBy).toLowerCase();
    const loggedIn   = (this.rebarAuth.getUserName() ?? '').toLowerCase();
    const rcMgr      = (this.rcManager          ?? '').toLowerCase();
    const secRcMgr   = (this.secondaryRcManager ?? '').toLowerCase();
    if (!createdBy || !loggedIn) return false;
    const primarySelf   = !!rcMgr    && createdBy === rcMgr    && loggedIn === rcMgr;
    const secondarySelf = !!secRcMgr && createdBy === secRcMgr && loggedIn === secRcMgr;
    return primarySelf || secondarySelf;
  }

  /**
   * Returns the form type based on requestType.
   * Add new cases here for new request types.
   */
 get formType(): 'addUser' | 'aoslRequest' | 'reviewCancellation' | 'notApplicable' | 'revisedTargetDate' | 'default' {
    const type = this.requestType.toLowerCase();
    if (this.requestType === 'Add New User') return 'addUser';
    if (this.requestType === 'Accenture Operations Service Line' || this.requestType === 'Accenture Operation Service Line') return 'aoslRequest';
    if (type.includes('review cancellation')) return 'reviewCancellation';
    if (type.includes('not applicable')) return 'notApplicable';
    if (type.includes('revised target publication date')) return 'revisedTargetDate';
    return 'default';
  }

  rcFunctions = ['Reviewer', 'Manager', 'Supervisor', 'Admin'];
  countries = ['India', 'United States', 'United Kingdom', 'Australia', 'Canada'];
  workLocations = ['Mumbai', 'Bengaluru', 'Delhi', 'Hyderabad', 'Chennai', 'Pune'];

  addNewUserForm: AddUserForm = {
    requestType: '',
    email: '',
    firstName: '',
    lastName: '',
    rcFunction: '',
    country: '',
    workLocation: '',
    expirationOn: '',
  };

  requestDetailsForm: RequestDetailsForm = {
    reviewId: '',
    reason: '',
    attachments: [] as { fileName: string; storagePath: string }[],
    requestedBy: '',
    requestedDateTime: '',
    createdDateTime: '',
    modifiedDateTime: '',
    createdBy: '',
    modifiedBy: '',
    status: '',
    revisedTargetPublicationDate: ''
  }

  requestedBy = '';
  requestedDate = '';

  aoslForm = {
    requestType: 'Accenture Operations Service Line',
    name: '',
    contractNumber: '',
    masterCustomerNumber: '',
    market: '',
    marketUnit: '',
    comments: '',
    attachments: [] as { fileName: string; storagePath: string }[],
    requestedBy: '',
    requestedDateTime: '',
    createdDateTime: '',
    modifiedDateTime: '',
    createdBy: '',
    modifiedBy: '',
  };

  aoslMarkets: string[] = [];
  aoslMarketUnits: string[] = [];

  showApproveModal = false;
  acknowledged = false;

  showRejectModal = false;
  rejectionReason = '';

  isNavigatingToReview = false;
  isLoading = false;
  status = '';
  private rcManager = '';
  private secondaryRcManager = '';

  private roleList: any[] = [];

  ngOnInit(): void {
    this._pickerResizeHandler = () => {
      if (this._activePicker && !this._activePicker.disabled && !this._activePicker.readOnly) {
        const picker = this._activePicker;
        picker.blur();
        setTimeout(() => picker.showPicker?.(), 0);
      }
    };
    window.addEventListener('resize', this._pickerResizeHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', this._pickerResizeHandler!));

  const state = history.state ?? {};
  const qp = this.route.snapshot.queryParamMap;

  this.requestType =
    state['requestType'] ?? qp.get('requestType') ?? this.requestType;

  this.mode =
    (state['mode'] ?? qp.get('mode') ?? 'view') as 'view' | 'edit';

  this.requestId = state['requestId'] ?? qp.get('requestId') ?? this.requestId;

  this.addNewUserForm.requestType = this.requestType;
  const requestData = this.normalizeResponse(state['requestData']);

  if (requestData) {
    this.rawRequestData = requestData;
    this.populateFromResponse(requestData);
  } else if (this.requestId) {
    this.isLoading = true;
    this.masterService.getRequestData(this.requestId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const data = this.normalizeResponse(res);
          if (data) {
            this.rawRequestData = data;
            this.populateFromResponse(data);
          }
          this.isLoading = false;
        },
        error: (err: any) => {
          console.error('Failed to load request data:', err?.status, err?.message);
          this.isLoading = false;
        }
      });
  }

  if (this.formType === 'addUser') {
    this.masterService.getRoleNames()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res: any[]) => { this.roleList = res || []; } });
  }
}

  /** API may return a single object or an array containing one record. */
  private normalizeResponse(data: any): any {
    if (!data) return null;
    if (Array.isArray(data)) return data.length ? data[0] : null;
    return data;
  }

  /** Maps the API response onto the form models based on the request type. */
  private populateFromResponse(res: any): void {
    this.requestId = `R-${res.RequestID ?? this.requestId}`;
    this.requestType = res.RequestType ?? this.requestType;
    this.addNewUserForm.requestType = this.requestType;
    this.status = res.Status ?? this.status;

    if (this.formType === 'addUser') {
      this.addNewUserForm.email = res.EmailEnterpriseID ?? '';
      this.addNewUserForm.firstName = res.FirstName ?? '';
      this.addNewUserForm.lastName = res.LastName ?? '';
      this.addNewUserForm.rcFunction = res.RCFunctionDesignation ?? '';
      this.addNewUserForm.country = this.ensureOption(this.countries, res.Country);
      this.addNewUserForm.workLocation = this.ensureOption(this.workLocations, res.WorkLocation);
      this.addNewUserForm.expirationOn = this.toDateInput(res.ExpirationOn);
      this.requestedBy = this.beforeAt(res.RequestedBy);
      this.requestedDate = this.formatDateTime(res.RequestedDateTime);
    } else if (this.formType === 'aoslRequest') {
      this.aoslForm.name = res.OperationServiceLine ?? '';
      this.aoslForm.contractNumber = res.ContractNumber ?? '';
      this.aoslForm.masterCustomerNumber = res.MasterCustomerNumber ?? '';
      this.aoslForm.market = this.ensureOption(this.aoslMarkets, res.Market);
      this.aoslForm.marketUnit = this.ensureOption(this.aoslMarketUnits, res.MarketUnit);
      this.aoslForm.comments = res.Comments ?? '';
      this.aoslForm.attachments = this.extractAttachmentNames(res.Documents);
      this.aoslForm.requestedBy = this.beforeAt(res.RequestedBy);
      this.aoslForm.requestedDateTime = this.formatDateTime(res.RequestedDateTime);
      this.aoslForm.createdDateTime = this.formatDateTime(res.CreatedDateTime);
      this.aoslForm.modifiedDateTime = this.formatDateTime(res.ModifiedDateTime);
      this.aoslForm.createdBy = this.beforeAt(res.CreatedBy);
      this.aoslForm.modifiedBy = res.ModifiedBy ?? '';
    } else {
      this.requestDetailsForm.reviewId = `R-${res.ReviewID ?? ''}`;
      this.requestDetailsForm.reason =
        res.CancellationReason ?? res.NotApplicableReason ?? res.RevisedDateReason ?? '';
      this.requestDetailsForm.attachments = this.extractAttachmentNames(res.Documents);
      this.requestDetailsForm.requestedBy = this.beforeAt(res.RequestedBy);
      this.requestDetailsForm.requestedDateTime = this.formatDateTime(res.RequestedDateTime);
      this.requestDetailsForm.createdDateTime = this.formatDateTime(res.CreatedDateTime);
      this.requestDetailsForm.modifiedDateTime = this.formatDateTime(res.ModifiedDateTime);
      this.requestDetailsForm.createdBy = res.CreatedBy ?? '';
      this.requestDetailsForm.modifiedBy = res.ModifiedBy ?? '';
      this.requestDetailsForm.status = res.Status ?? '';
      this.requestDetailsForm.revisedTargetPublicationDate = this.toDateInput(res.RevisedTargetPublicationDate);
      try {
        const reviewDetails = typeof res.ReviewDetails === 'string'
          ? JSON.parse(res.ReviewDetails) : (res.ReviewDetails ?? []);
        this.rcManager          = reviewDetails[0]?.RCManager          ?? '';
        this.secondaryRcManager = reviewDetails[0]?.SecondaryRCManager ?? reviewDetails[0]?.SecondaryRcManager ?? '';
      } catch { this.rcManager = ''; this.secondaryRcManager = ''; }
    }
  }

  /** Ensures a value coming from the API is selectable in a dropdown. */
  private ensureOption(list: string[], value: any): string {
    const val = (value ?? '').toString().trim();
    if (val && !list.includes(val)) {
      list.push(val);
    }
    return val || list[0];
  }

  private beforeAt(value: any): string {
    const s = (value ?? '').toString();
    const idx = s.indexOf('@');
    return idx > 0 ? s.substring(0, idx) : s;
  }

  private extractAttachmentNames(documents: any): { fileName: string; storagePath: string }[] {
    if (!documents) return [];
    try {
      const docs = typeof documents === 'string' ? JSON.parse(documents) : documents;
      return Array.isArray(docs)
        ? docs
            .map((d: any) => ({ fileName: d.FileName ?? '', storagePath: d.StorageFilePath ?? '' }))
            .filter((d: { fileName: string }) => d.fileName)
        : [];
    } catch {
      return [];
    }
  }

  downloadDocument(doc: { fileName: string; storagePath: string }): void {
    this.reviewerService.downloadIssueDocument(doc.storagePath)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.fileName || doc.storagePath.split('/').pop() || 'download';
          a.click();
          URL.revokeObjectURL(url);
        },
        error: (err: any) => console.error('Download error:', err?.status, err?.message),
      });
  }

  /** Converts an ISO date string to yyyy-MM-dd for date inputs. */
  private toDateInput(value: any): string {
    if (!value) return '';
    const s = value.toString();
    return s.length >= 10 ? s.substring(0, 10) : '';
  }

  /** Formats an ISO date as dd-Mon-yyyy @hh:mm AM/PM. */
  private formatDateTime(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value.toString();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${d.getDate()}-${months[d.getMonth()]}-${d.getFullYear()} @${hours}:${minutes} ${ampm}`;
  }

  navigateToReview(): void {
    const reviewId = Number(this.requestDetailsForm.reviewId.replace(/^R-/i, ''));
    if (!reviewId) return;
    window.open(`/review-management/review-process?reviewId=${reviewId}&entryMode=action`, '_blank');
  }

  onCancel() {
    this.router.navigate(['/request']);
  }

  onReject() {
    // Generated by GitHub Copilot - Oct-07-2026
    if (this.isViewMode || this.isRequestCompleted) return;
    this.rejectionReason = '';
    this.showRejectModal = true;
    this.setScrollLock(true);
  }

  closeRejectModal() {
    this.showRejectModal = false;
    this.setScrollLock(false);
  }

  // Freeze the background page while a confirmation popup is open
  private setScrollLock(lock: boolean) {
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  private unlockScrollOnDestroy = this.destroyRef.onDestroy(() => this.setScrollLock(false));

  confirmReject() {
  // Generated by GitHub Copilot - Oct-07-2026
  if (this.isViewMode || this.isRequestCompleted) return;
  if (!this.rejectionReason.trim()) {
    return;
  }

  this.showRejectModal = false;
  this.setScrollLock(false);
  this.isLoading = true;

  const payload = this.buildPayload({

    Status: 'Rejected',

    CancellationReason: this.rejectionReason,

    AcknowledgementAccepted: false,

    AcknowledgementText: null,
    // The user who performed the action (who rejected the request) - Added for email notifications
    ActionedBy: this.rebarAuth.getUserName() ?? ''
  });

  this.masterService.updateRequestList(payload).subscribe({

    next: (response) => {
      this.status = 'Rejected';
      this.requestDetailsForm.status = 'Rejected';
      this.router.navigate(['/request'], { state: { rejectSuccess: true } });
    },

    error: (err) => {
      console.error(err);
      this.isLoading = false;
    }

  });

}

  onApprove() {
    // Generated by GitHub Copilot - Oct-07-2026
    if (this.isViewMode || this.isRequestCompleted) return;
    this.acknowledged = false;
    this.showApproveModal = true;
    this.setScrollLock(true);
  }

  closeApproveModal() {
    this.showApproveModal = false;
    this.setScrollLock(false);
  }

  confirmApprove() {
  // Generated by GitHub Copilot - Oct-07-2026
  if (this.isViewMode || this.isRequestCompleted) return;
  if (!this.acknowledged) {
    return;
  }

  this.showApproveModal = false;
  this.setScrollLock(false);
  this.isLoading = true;

  const payload = this.buildPayload({
    Status: 'Approved',
    AcknowledgementAccepted: true,
    AcknowledgementText:
      'I hereby acknowledge that I have thoroughly reviewed the request and am making an informed decision',
         
    // The user who performed the action (who approved the request) - Added for email notifications
    ActionedBy: this.rebarAuth.getUserName() ?? ''
  });
  this.masterService.updateRequestList(payload).subscribe({
    next: () => {
      this.status = 'Approved';
      this.requestDetailsForm.status = 'Approved';
      if (this.formType === 'addUser') {
        this.insertUserAfterApprove();
      } else {
        this.router.navigate(['/request'], { state: { approveSuccess: true } });
      }
    },
    error: (err) => {
      console.error(err);
      this.isLoading = false;
    }
  });
}

  private insertUserAfterApprove(): void {
    const email = this.addNewUserForm.email;
    const roleId = this.getRoleId(this.addNewUserForm.rcFunction);
    this.masterService.insertUser({
      Email: email,
      FirstName: this.addNewUserForm.firstName,
      LastName: this.addNewUserForm.lastName,
      EnterpriseID: email.includes('@') ? email.split('@')[0] : email,
      RoleID: roleId,
      Country: this.addNewUserForm.country,
      WorkLocation: this.addNewUserForm.workLocation,
      ExpirationOn: this.addNewUserForm.expirationOn || null,
      ReasonForReject: '',
      IsActive: 1,
      LoggedUserID: this.rebarAuth.getUser() ?? '',
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.router.navigate(['/request'], { state: { approveSuccess: true } });
      },
      error: (err) => {
        console.error('InsertUser error:', err);
        this.isLoading = false;
      },
    });
  }

  private getRoleId(roleName: string): number | null {
    const role = this.roleList.find(r =>
      (r.RoleName ?? r.Role_Name ?? r.roleName ?? r.Name ?? r.name ?? '') === roleName
    );
    return role ? (role.Role_ID ?? role.RoleID ?? role.roleId ?? role.RoleId ?? null) : null;
  }

  private buildPayload(overrides: Record<string, any>): any {

  if (!this.rawRequestData) {
    return {};
  }

 const payload = {
  ...this.rawRequestData,

  DocumentsJSON: JSON.stringify(this.rawRequestData.Documents ?? []),

  ModifiedBy: this.rawRequestData.RequestedBy,

  ...overrides
};
  delete payload.Documents;
  return payload;
}

  
}
